import api from './api';

/**
 * Image Service
 * Handles client-side interactions with the image generation API
 * Supports OpenAI's dall-e-3 and dall-e-2 models
 */
const imageService = {
  /**
   * Generate an image from a text prompt
   * @param {string} prompt - The text prompt describing the desired image
   * @param {Object} options - Additional options for image generation
   * @param {string} options.model - Model to use (dall-e-3, dall-e-2, gpt-image-1)
   * @param {number} options.n - Number of images to generate
   * @param {string} options.size - Image size (e.g., 1024x1024, 1024x1792, 1792x1024)
   * @param {string} options.quality - Image quality (standard or hd)
   * @param {string} options.style - Image style (natural, vivid)
   * @param {string} options.response_format - Response format (url, b64_json)
   * @param {string} options.user - Unique user identifier for tracking
   * @returns {Promise<Object>} - The generated image data
   */
  generateImage: async (prompt, options = {}) => {
    try {
      // Determine if we're using GPT Image 1 which requires special handling
      const isGptImage1 = options.model === 'gpt-image-1';
      
      // Default to b64_json response format if not specified (and if not GPT Image 1)
      const modifiedOptions = {
        ...options
      };
      
      // Only add response_format for DALL-E models, not for GPT Image 1
      if (!isGptImage1) {
        modifiedOptions.response_format = options.response_format || 'b64_json';
      }
      
      console.log(`Generating image with model: ${options.model || 'dall-e-3'}`);
      
      // Use a generous timeout for image generation requests
      const requestConfig = {
        timeout: 600000, // 10 minutes timeout for image generation
      };
      
      const response = await api.post('/images/generate', {
        prompt,
        ...modifiedOptions
      }, requestConfig);
      
      // Check if this is a streaming response
      const isStreaming = response.data.streaming === true;
      const requestId = response.data.requestId;
      
      if (isStreaming && requestId) {
        console.log(`Received streaming response with requestId: ${requestId}`);
        
        // For streaming responses, return the data with the requestId and processing flag
        return { 
          streaming: true,
          requestId,
          data: response.data.data
        };
      }
      
      // For non-streaming responses, process normally
      const responseData = response.data.data;
      
      // Image data processing happens on the backend,
      // we just need to return the processed data from the API
      
      // Make sure we always return an array of image objects with urls
      if (Array.isArray(responseData)) {
        return { data: responseData };
      } else if (responseData && typeof responseData === 'object') {
        return { data: [responseData] };
      } else {
        console.warn('Unexpected response format from image generation API');
        return { data: [] };
      }
    } catch (error) {
      console.error('Image generation error:', error);
      
      // Preserve the full error object to maintain error.response data in components
      if (error.response && error.response.data) {
        const errorMessage = error.response.data.message || 'Failed to generate image';
        
        // Enhance the error object with more details from the API
        const enhancedError = new Error(errorMessage);
        enhancedError.response = error.response;
        enhancedError.status = error.response.status;
        enhancedError.data = error.response.data;
        
        throw enhancedError;
      }
      
      // If it's an OpenAI API key missing error
      if (error.response && error.response.status === 500 && 
          error.response.data && error.response.data.message && 
          error.response.data.message.includes('API key')) {
        const keyError = new Error('OpenAI API key is not configured');
        keyError.isConfigError = true;
        throw keyError;
      }
      
      // For other errors, preserve the original error
      throw error;
    }
  },

  /**
   * Edit an existing image with a prompt and optional mask
   * @param {string} imageUrl - URL of the source image to edit
   * @param {string} prompt - Text prompt describing the desired changes
   * @param {string|null} maskUrl - Optional URL of the mask image (transparent areas will be edited)
   * @param {Object} options - Additional options for editing
   * @param {string} options.model - Model to use (only dall-e-2 is supported)
   * @param {number} options.n - Number of images to generate (1-10)
   * @param {string} options.size - Image size (must match source image dimensions)
   * @param {string} options.response_format - Response format (url, b64_json)
   * @param {string} options.user - Unique user identifier for tracking
   * @returns {Promise<Object>} - The edited image data
   */
  editImage: async (imageUrl, prompt, maskUrl = null, options = {}) => {
    try {
      // Default to b64_json response format if not specified
      const modifiedOptions = {
        ...options,
        response_format: options.response_format || 'b64_json'
      };
      
      const response = await api.post('/images/edit', {
        imageUrl,
        prompt,
        mask: maskUrl,
        ...modifiedOptions
      });
      
      // Return the processed data - the backend already converts b64_json to URLs
      return response.data.data;
    } catch (error) {
      console.error('Image editing error:', error);
      
      // Handle and enhance error objects similar to generateImage
      if (error.response && error.response.data) {
        const errorMessage = error.response.data.message || 'Failed to edit image';
        
        const enhancedError = new Error(errorMessage);
        enhancedError.response = error.response;
        enhancedError.status = error.response.status;
        enhancedError.data = error.response.data;
        
        throw enhancedError;
      }
      
      throw error;
    }
  },

  /**
   * Generate variations of an existing image
   * @param {string} imageUrl - URL of the source image
   * @param {Object} options - Additional options (model, n, size, etc.)
   * @returns {Promise<Object>} - The generated image variations
   */
  generateImageVariations: async (imageUrl, options = {}) => {
    try {
      // Default to b64_json response format if not specified
      const modifiedOptions = {
        ...options,
        response_format: options.response_format || 'b64_json'
      };
      
      const response = await api.post('/images/variations', {
        imageUrl,
        ...modifiedOptions
      });
      
      // Return the processed data - the backend already converts b64_json to URLs
      return response.data.data;
    } catch (error) {
      console.error('Image variation error:', error);
      
      // Handle and enhance error objects similar to other methods
      if (error.response && error.response.data) {
        const errorMessage = error.response.data.message || 'Failed to generate image variations';
        
        const enhancedError = new Error(errorMessage);
        enhancedError.response = error.response;
        enhancedError.status = error.response.status;
        enhancedError.data = error.response.data;
        
        throw enhancedError;
      }
      
      throw error;
    }
  },

  /**
   * Save a generated image to a project
   * @param {string} projectId - ID of the project
   * @param {string} imageUrl - URL of the generated image
   * @param {string} prompt - Text prompt used to generate the image
   * @param {Object} metadata - Additional metadata about the image
   * @returns {Promise<Object>} - The saved image data
   */
  saveImageToProject: async (projectId, imageUrl, prompt, metadata = {}) => {
    try {
      const response = await api.post(`/images/projects/${projectId}`, {
        imageUrl,
        prompt,
        metadata
      });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to save image to project';
    }
  },

  /**
   * Get all images for a project
   * @param {string} projectId - ID of the project
   * @param {Object} options - Filter options (category, favorites, tag, etc.)
   * @returns {Promise<Array>} - Array of project images
   */
  getProjectImages: async (projectId, options = {}) => {
    try {
      // Build query string from options
      const queryParams = new URLSearchParams();
      
      if (options.category) {
        queryParams.append('category', options.category);
      }
      
      if (options.favorites === true) {
        queryParams.append('favorites', 'true');
      }
      
      if (options.tag) {
        queryParams.append('tag', options.tag);
      }
      
      if (options.limit) {
        queryParams.append('limit', options.limit.toString());
      }
      
      const queryString = queryParams.toString();
      const url = `/images/projects/${projectId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to get project images';
    }
  },
  
  /**
   * Get image categories for a project
   * @param {string} projectId - ID of the project
   * @returns {Promise<Array>} - Array of category objects with counts
   */
  getImageCategories: async (projectId) => {
    try {
      const response = await api.get(`/images/projects/${projectId}/categories`);
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to get image categories';
    }
  },
  
  /**
   * Update an image's metadata
   * @param {string} imageId - ID of the image to update
   * @param {Object} updateData - Data to update (category, tags, isFavorite)
   * @returns {Promise<Object>} - Updated image data
   */
  updateImage: async (imageId, updateData) => {
    try {
      const response = await api.put(`/images/${imageId}`, updateData);
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update image';
    }
  },
  
  /**
   * Delete an image
   * @param {string} imageId - ID of the image to delete
   * @returns {Promise<boolean>} - True if successful
   */
  deleteImage: async (imageId) => {
    try {
      const response = await api.delete(`/images/${imageId}`);
      return response.data.success;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete image';
    }
  },
  
  /**
   * Check the status of an in-progress image generation request
   * This is used for GPT Image 1 model which may take longer to generate
   * @param {string} requestId - The ID of the image generation request
   * @returns {Promise<Object>} - The status of the image generation request
   */
  /**
   * Generate an image using reference images
   * @param {string} prompt - The text prompt describing the desired image
   * @param {Array<Object>} referenceImages - Array of image objects with URLs
   * @param {Object} options - Additional options for image generation
   * @returns {Promise<Object>} - The generated image data
   */
  generateFromReferences: async (prompt, referenceImages, options = {}) => {
    try {
      console.log(`Generating image with ${referenceImages.length} reference images`);
      
      // Only GPT Image 1 supports reference images
      const modifiedOptions = {
        ...options,
        model: 'gpt-image-1', // Force model to gpt-image-1 which supports references
      };
      
      // Use a generous timeout for image generation requests
      const requestConfig = {
        timeout: 600000, // 10 minutes timeout for image generation
      };
      
      // Format request according to OpenAI documentation
      const response = await api.post('/images/generate', {
        prompt,
        model: modifiedOptions.model,
        size: modifiedOptions.size || '1024x1024',
        n: modifiedOptions.n || 1,
        image_references: referenceImages, // Using the correct format for reference images
        ...modifiedOptions
      }, requestConfig);
      
      // Check if this is a streaming response
      const isStreaming = response.data.streaming === true;
      const requestId = response.data.requestId;
      
      if (isStreaming && requestId) {
        console.log(`Received streaming response with requestId: ${requestId}`);
        
        // For streaming responses, return the data with the requestId and processing flag
        return { 
          streaming: true,
          requestId,
          data: response.data.data
        };
      }
      
      // For non-streaming responses, process normally
      const responseData = response.data.data;
      
      // Make sure we always return an array of image objects with urls
      if (Array.isArray(responseData)) {
        return { data: responseData };
      } else if (responseData && typeof responseData === 'object') {
        return { data: [responseData] };
      } else {
        console.warn('Unexpected response format from image generation API');
        return { data: [] };
      }
    } catch (error) {
      console.error('Reference image generation error:', error);
      
      // Preserve the full error object to maintain error.response data in components
      if (error.response && error.response.data) {
        const errorMessage = error.response.data.message || 'Failed to generate image from references';
        
        const enhancedError = new Error(errorMessage);
        enhancedError.response = error.response;
        enhancedError.status = error.response.status;
        enhancedError.data = error.response.data;
        
        throw enhancedError;
      }
      
      throw error;
    }
  },
  
  checkImageStatus: async (requestId) => {
    try {
      console.log(`Checking image status for requestId: ${requestId}`);
      
      // No artificial delay - let the polling interval in the hook handle timing
      
      // Use a shorter timeout for status checks to avoid blocking
      const statusConfig = {
        timeout: 10000, // 10 seconds timeout for status checks
      };
      
      const response = await api.get(`/images/status/${requestId}`, statusConfig);
      
      // Check if we got a success: true response but need to extract data
      if (response.data.success === true) {
        const statusData = {
          completed: response.data.completed,
          error: response.data.error,
          progress: response.data.progress,
          data: response.data.data,
          message: response.data.message,
          status: response.data.status,
          requestId: requestId
        };
        
        // Log the extracted status data
        console.log(`Status response for ${requestId}:`, {
          completed: statusData.completed,
          error: statusData.error,
          progress: statusData.progress,
          dataExists: !!statusData.data,
          message: statusData.message
        });
        
        return statusData;
      }
      
      // Log the response for debugging
      console.log(`Status response for ${requestId}:`, {
        completed: response.data.completed,
        error: response.data.error,
        progress: response.data.progress,
        dataExists: !!response.data.data
      });
      
      return response.data;
    } catch (error) {
      console.error('Error checking image status:', error);
      
      // Check if this is a network error
      if (!error.response) {
        return {
          completed: false,
          status: 'network_error',
          error: 'Network error checking image status - will retry'
        };
      }
      
      // Return a standard format even on error
      return {
        completed: false,
        status: 'error',
        error: error.response?.data?.message || 'Failed to check image status'
      };
    }
  }
};

export default imageService;